import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { Loader2, Calendar, ArrowLeft, Tag } from 'lucide-react';

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const NewsDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await apiClient.get(`/api/blog/slug/${slug}`, { skipAuth: true });
        setPost(data);
        document.title = `${data.seo_title || data.title} — Deus Estate`;
        if (data.seo_description) {
          let meta = document.querySelector('meta[name="description"]');
          if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'description';
            document.head.appendChild(meta);
          }
          meta.content = data.seo_description;
        }
      } catch {
        setPost(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" /></div>;
  }
  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-20 container-luxury text-center">
        <h1 className="text-3xl font-serif text-[#F6F2E8] mb-4">Статията не е намерена</h1>
        <Link to="/news" className="text-[#D4AF37] hover:underline">← Назад към всички статии</Link>
      </div>
    );
  }

  return (
    <article className="min-h-screen pt-28 pb-20" data-testid="news-detail">
      <div className="container-luxury max-w-4xl">
        <Link to="/news" className="inline-flex items-center gap-2 text-[#A9A9A9] hover:text-[#D4AF37] mb-6">
          <ArrowLeft size={16} /> <span>Всички статии</span>
        </Link>

        {post.featured_image && (
          <div className="aspect-[16/8] rounded-2xl overflow-hidden border border-[#D4AF37]/10 mb-8">
            <img
              src={resolveUrl(post.featured_image)}
              alt={post.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.parentElement.style.display = 'none';
              }}
            />
          </div>
        )}

        <header className="mb-8 space-y-4">
          <div className="flex items-center gap-4 text-sm text-[#A9A9A9]">
            <span className="flex items-center gap-1"><Calendar size={14} />{new Date(post.published_at || post.created_at).toLocaleDateString('bg-BG', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span>•</span>
            <span>{post.views_count || 0} прегледа</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-semibold text-[#F6F2E8] leading-tight" data-testid="news-detail-title">{post.title}</h1>
          {post.excerpt && <p className="text-xl text-[#A9A9A9] leading-relaxed">{post.excerpt}</p>}
        </header>

        <div
          className="prose prose-invert prose-lg max-w-none text-[#A9A9A9] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {post.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-[#D4AF37]/10 flex items-center gap-2 flex-wrap">
            <Tag size={14} className="text-[#D4AF37]" />
            {post.tags.map(t => (
              <span key={t} className="text-xs px-3 py-1 bg-[#111111] text-[#D4AF37] rounded-full">{t}</span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default NewsDetail;
